import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The type ramp's role names. `theme.css` closes the `--text-*` namespace and
 * reopens it with exactly these — the thirteen published sizes, then the
 * twelve dense variants that add no new size, only the line-heights a 22 px
 * calendar bar and a one-line total rule need. Nothing between them exists.
 *
 * Every one of them has to be listed here or it is not a role: `twMerge`
 * reads an unregistered `text-*` as a COLOUR, and the merge that keeps
 * `className` winning stops happening — silently, with the class simply
 * absent from the rendered markup.
 */
const TYPE_ROLES = [
  'readout',
  'readout-2',
  'readout-3',
  'headline',
  'title',
  'title-2',
  'subject',
  'body',
  'body-compact',
  'data',
  'support',
  'label',
  'chip',
  'passcode',
  'figure',
  'figure-2',
  'figure-3',
  'datemark',
  'datemark-2',
  'subject-2',
  'body-fine',
  'ghost',
  'tile',
  'support-2',
  'tile-2',
] as const;

/** The four radii. There is no fifth, and nothing is rounder than 6 px. */
const RADII = ['band', 'chip', 'control', 'panel'] as const;

/** Three flat elevation steps, plus the two pressed states of the key bevel. */
const SHADOWS = ['flush', 'key', 'key-pressed', 'key-sm', 'key-sm-pressed', 'drawer'] as const;

const TRACKING = ['normal', 'readout', 'readout-2', 'headline', 'label'] as const;

/**
 * The named sizes: structural facts of the two shells rather than free values.
 * They have to be in the sizing groups or a caller's `className` override sits
 * beside the component's own class instead of replacing it, and the winner is
 * decided by stylesheet order — which no caller can reason about.
 */
const NAMED_SIZES = [
  'hair',
  'touch',
  'touch-lg',
  'control',
  'field',
  'field-lg',
  'field-cell',
  'key',
  'key-sm',
  'key-landscape',
  'rail',
  'rail-short',
  'band',
  'band-quiet',
  'nav',
  'nav-item',
  'nav-subitem',
  'row',
  'row-compact',
  'sidebar',
  'sidebar-rail',
  'sidebar-drawer',
  'drawer',
  'dialog',
  'dialog-wide',
  'dialog-workbench',
  'worker-column',
  'content-max',
] as const;

/**
 * `tailwind-merge` has to be told about this theme.
 *
 * Out of the box it classifies any unknown `text-*` as a COLOUR, so
 * `cn('text-chip', 'text-status-info-fg')` silently deletes `text-chip` and the
 * chip renders at the inherited size. That is not hypothetical: it is how the
 * first status chip in this package lost its 10 px role, and nothing about the
 * rendered markup said so — the class was simply absent.
 *
 * The same applies to every custom scale. A theme that renames `--radius-*`,
 * `--shadow-*` and half the spacing scale has to hand those names back, or the
 * "className always wins" contract this package advertises is false.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [...TYPE_ROLES] }],
      rounded: [{ rounded: [...RADII] }],
      shadow: [{ shadow: [...SHADOWS] }],
      tracking: [{ tracking: [...TRACKING] }],
      h: [{ h: [...NAMED_SIZES] }],
      'min-h': [{ 'min-h': [...NAMED_SIZES] }],
      'max-h': [{ 'max-h': [...NAMED_SIZES] }],
      w: [{ w: [...NAMED_SIZES] }],
      'min-w': [{ 'min-w': [...NAMED_SIZES] }],
      'max-w': [{ 'max-w': [...NAMED_SIZES] }],
      size: [{ size: [...NAMED_SIZES] }],
    },
  },
});

/**
 * Compose class names and let the *last* Tailwind utility in a conflicting
 * group win.
 *
 * Without `twMerge`, `cn('px-4', props.className)` where the caller passes
 * `px-6` leaves both in the attribute and the winner is decided by stylesheet
 * order. Every component in this package puts `className` last so a consumer
 * can always override.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export type { ClassValue };
